import type {StoreApi} from 'zustand/vanilla';

import {isNotNil} from 'ramda';

import type {Nullish} from 'shared/web';

import type {
  ActivePreset,
  FeGridSettings,
  GetDataQueryResponse,
  Preset,
  VirtualPreset,
} from '../../types/Api';
import type {Filters} from '../../types/Filters';
import type {QueryFilterObject} from '../../types/QueryFilterObject';
import {loadLocalGridSettings, saveLocalGridSettings} from '../../utils/presetLocalPrefs';
import type {EagGridState, EagGridStore} from '../createEagGridStore';

type DataConfigActions = Pick<
  EagGridStore,
  | 'setFilterValues'
  | 'setStaticFilters'
  | 'resetFilterValues'
  | 'setGridSettings'
  | 'setActivePreset'
  | 'applyActivePresetTransition'
  | 'setCustomPresets'
  | 'setVirtualPreset'
  | 'setIsPresetChanged'
  | 'setIsPresetCopyInProgress'
  | 'setActivePresetDataQuery'
>;

/** Filter + preset / grid-settings actions. Merged from createFilterActions and createPresetActions. */
export function createDataConfigActions(store: StoreApi<EagGridState>): DataConfigActions {
  return {
    setFilterValues: (filterValues: Filters) => {
      if (store.getState().filterValues === filterValues) {
        return;
      }
      store.setState({filterValues});
    },

    setStaticFilters: (staticFilters: QueryFilterObject) => {
      if (store.getState().staticFilters === staticFilters) {
        return;
      }
      store.setState({staticFilters});
    },

    resetFilterValues: () => {
      const state = store.getState();
      store.setState({
        // Atomic shortcut: equivalent to startFetch() but folded into the same
        // setState as the filter reset so subscribers see one update.
        fetchState: 'loading',
        previousRows: state.rows,
        // Smart search is cleared too: the ActiveFilterBar renders it as a
        // removable chip, so "Clear all" must not leave it behind (T20-130744
        // follow-up — a search-only bar made the button a visible no-op).
        filterValues: {},
      });
    },

    setGridSettings: (settings: Partial<FeGridSettings>) => {
      const state = store.getState();
      if (!state.gridSettings) {
        return;
      }
      // A page-size change makes the current page index potentially out of
      // range (e.g. page 6 at 25/page becomes an offset past the last row at
      // 200/page). Snap back to the first page so the fetch reaction requests a
      // valid offset, and clear row selection the same way page navigation does
      // (useEagGridHandlers.handlePageChange) — otherwise IDs selected on the
      // previous page stay selected and actionable while their rows are
      // off-screen. Page-size lives only in the SettingsPanel now, so this
      // reset also has to happen here (the panel never reset the page before).
      const pageSizeChanged =
        isNotNil(settings.itemsPerPage) &&
        settings.itemsPerPage !== state.gridSettings.itemsPerPage;
      // Only rebuild the selection Set when there is something to clear, to keep
      // its reference stable for selection subscribers (mirrors deselectAll's
      // no-op guard).
      const shouldClearSelection = pageSizeChanged && state.selectedRowIds.size > 0;
      if (state.gridCode) {
        saveLocalGridSettings(state.gridCode, settings);
      }
      store.setState({
        gridSettings: {...state.gridSettings, ...settings},
        ...(pageSizeChanged ? {page: 0, rowSelectionAnchorId: null} : {}),
        ...(shouldClearSelection
          ? {selectedRowIds: new Set<string>(), bulkSelectionQueryId: null}
          : {}),
      });
    },

    setActivePreset: (activePreset: ActivePreset | Nullish) => {
      store.setState({
        activePreset,
        // Seed the working copy from the preset; keep the current copy when a
        // caller clears the preset (never happens today, but stay non-destructive).
        gridSettings: activePreset?.gridSettings
          ? {...activePreset.gridSettings, ...loadLocalGridSettings(store.getState().gridCode)}
          : store.getState().gridSettings,
      });
    },

    /**
     * Atomic preset transition: queryId + activePreset (with its gridSettings
     * reseed, mirroring setActivePreset) land in ONE setState.
     *
     * Setting queryId in its own dispatch first (the old two-step
     * applyPresetQuerySync) fired GridLifecycle's dirty/PUT reaction while the
     * PREVIOUS preset was still active: activating a custom preset from the
     * virtual view ran the reaction's virtual branch with the custom preset's
     * queryId already in the URL/store — and PUT the virtual preset with the
     * custom's dataQueryId, permanently pointing the Default view at the
     * custom preset's filters (T20-134468).
     */
    applyActivePresetTransition: (queryId: string, activePreset: ActivePreset) => {
      store.setState({
        queryId,
        bulkSelectionQueryId: null,
        rowSelectionAnchorId: null,
        activePreset,
        gridSettings: activePreset.gridSettings
          ? {...activePreset.gridSettings, ...loadLocalGridSettings(store.getState().gridCode)}
          : store.getState().gridSettings,
      });
    },

    setCustomPresets: (customPresets: Preset[] | null) => {
      store.setState({customPresets});
    },

    setVirtualPreset: (virtualPreset: VirtualPreset | Nullish) => {
      store.setState({virtualPreset});
    },

    setIsPresetChanged: (isPresetChanged: boolean) => {
      if (store.getState().isPresetChanged === isPresetChanged) {
        return;
      }
      store.setState({isPresetChanged});
    },

    setIsPresetCopyInProgress: (isPresetCopyInProgress: boolean) => {
      if (store.getState().isPresetCopyInProgress === isPresetCopyInProgress) {
        return;
      }
      store.setState({isPresetCopyInProgress});
    },

    setActivePresetDataQuery: (activePresetDataQuery: GetDataQueryResponse | Nullish) => {
      store.setState({activePresetDataQuery});
    },
  };
}
